import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, BookOpen } from "lucide-react";
import api from "@/lib/api";

function stripHtml(html) {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export default function BlogSection() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api
      .get("/blogs?limit=3")
      .then((r) => setPosts(r.data.slice(0, 3)))
      .catch(() => { });
  }, []);

  if (posts.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 my-20">
      {/* Header */}
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="ols-label mb-1">Insights &amp; Stories</div>
          <h2 className="font-display text-3xl md:text-4xl">
            From Our Journal
          </h2>
        </div>
        {/* <Link
          to="/blog"
          className="hidden md:flex items-center gap-1.5 text-sm font-medium text-stone-600 hover:text-stone-900 transition-colors"
        >
          All articles <ArrowRight className="w-4 h-4" />
        </Link> */}
      </div>

      {/* Blog cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
        {posts.map((post, i) => (
          <BlogCard key={post.id} post={post} featured={i === 0} />
        ))}
      </div>

      {/* Mobile view-all link */}
      <div className="flex md:hidden justify-center mt-8">
        <Link to="/blog" className="btn-outline text-sm">
          View all articles <ArrowRight className="w-4 h-4 ml-1" />
        </Link>
      </div>
    </section>
  );
}

function BlogCard({ post, featured }) {
  const excerpt = stripHtml(post.content || "").slice(0, 130).trim();

  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group flex flex-col rounded-2xl overflow-hidden border border-stone-100 hover:border-stone-200 hover:shadow-xl transition-all duration-300 bg-white"
      data-testid={`blog-card-${post.slug}`}
    >
      {/* Thumbnail */}
      <div className="aspect-[16/9] overflow-hidden bg-stone-100 relative">
        {post.featured_image ? (
          <img
            src={post.featured_image}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-stone-100 to-stone-200">
            <BookOpen className="w-10 h-10 text-stone-400" strokeWidth={1.5} />
          </div>
        )}
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-1">
        {/* Date */}
        <div className="flex items-center gap-1.5 text-stone-400 text-xs mb-3">
          <Calendar className="w-3 h-3" />
          <span>{formatDate(post.created_at)}</span>
        </div>

        {/* Title */}
        <h3 className="font-display text-lg leading-snug mb-2 text-stone-900 group-hover:text-stone-600 transition-colors duration-200 line-clamp-2">
          {post.title}
        </h3>

        {/* Excerpt */}
        {excerpt && (
          <p className="text-stone-500 text-sm leading-relaxed line-clamp-3 flex-1">
            {excerpt}
            {excerpt.length >= 130 && "…"}
          </p>
        )}

        {/* Read more */}
        <div className="flex items-center gap-1 mt-4 text-xs font-semibold text-stone-900 group-hover:gap-2 transition-all duration-200">
          Read article <ArrowRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </Link>
  );
}
